import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VideoPromotion = () => {
    // --- State ---
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // --- Refs ---
    const fileInputRef = useRef(null);

    // --- Hooks & Context ---
    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';

    // --- Role Verification Logic ---
    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN' || roleCode === 'sa';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Video Promotion",
            subtitle: "View and share promotional videos",
            uploadedBy: "Uploaded :",
            loadingData: "Loading Videos...",
            noData: "No promotional videos found.",
            btnAdd: "Add Video",
            btnChangeVideo: "Change Video",
            btnDelete: "Delete",
            uploading: "Uploading Video..."
        },
        hi: {
            pageTitle: "वीडियो प्रचार",
            subtitle: "प्रचार वीडियो देखें और साझा करें",
            uploadedBy: "अपलोड :",
            loadingData: "वीडियो लोड हो रहे हैं...",
            noData: "कोई प्रचार वीडियो नहीं मिला।",
            btnAdd: "वीडियो जोड़ें",
            btnChangeVideo: "वीडियो बदलें",
            btnDelete: "हटाएं",
            uploading: "अपलोड हो रहा है..."
        },
        gu: {
            pageTitle: "વિડિઓ પ્રમોશન",
            subtitle: "પ્રમોશનલ વિડિઓઝ જુઓ અને શેર કરો",
            uploadedBy: "અપલોડ :",
            loadingData: "વિડિઓઝ લોડ થઈ રહ્યા છે...",
            noData: "કોઈ પ્રમોશનલ વિડિઓઝ મળ્યા નથી.",
            btnAdd: "વિડિઓ ઉમેરો",
            btnChangeVideo: "વિડિઓ બદલો",
            btnDelete: "કાઢી નાખો",
            uploading: "અપલોડ થઈ રહ્યું છે..."
        }
    };

    const txt = t[lang] || t.gu;

    // --- Helper: Convert standard YouTube link to Embed Link ---
    const getYouTubeEmbedUrl = (url) => {
        if (!url) return "";
        let videoId = "";
        try {
            if (url.includes("youtu.be/")) {
                videoId = url.split("youtu.be/")[1].split("?")[0];
            } else if (url.includes("youtube.com/watch")) {
                videoId = url.split("v=")[1].split("&")[0];
            } else if (url.includes("youtube.com/embed/")) {
                videoId = url.split("embed/")[1].split("?")[0];
            } else if (url.includes("youtube.com/shorts/")) {
                videoId = url.split("shorts/")[1].split("?")[0];
            }
        } catch (e) {
            console.error("Error parsing YouTube URL", url);
        }
        return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    };

    // --- Check if URL is a direct video file ---
    const isDirectVideoFile = (url) => {
        if (!url) return false;
        const lowerUrl = url.toLowerCase();
        return lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.ogg') || lowerUrl.endsWith('.mov');
    };

    // --- API Fetch Logic ---
    const fetchVideos = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId?.toString(),
                    prachar_type: "video"
                }
            );

            const resData = response.data;
            if (resData.Success === "1" && Array.isArray(resData.result)) {
                setVideos(resData.result);
            } else if (resData.data && resData.data.result && Array.isArray(resData.data.result)) {
                setVideos(resData.data.result);
            } else {
                setVideos([]);
            }
        } catch (error) {
            console.error("Error fetching video promotions:", error);
            setVideos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    // --- Global Add / Change Action ---
    const handleAddVideoClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // --- CDN પર વિડિઓ અપલોડ કરીને ડેટાબેઝમાં એન્ટ્રી/અપડેટ કરવી ---
    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('api_key', 'BJP-Media-7Kx9Qm2pR4vN8wL3jH6tY5cF1bZ0aD');
            formData.append('app_id', appId?.toString() || "1");
            formData.append('user_id', userId?.toString() || "1");
            formData.append('folder', 'files');
            formData.append('file', file);

            console.log("Uploading video to CDN...");

            const cdnResponse = await axios.post(
                'https://cdn.bjpwings.com/ResourceUploadHandler.ashx',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            console.log("CDN Response:", cdnResponse.data);

            if (cdnResponse.data && cdnResponse.data.Success === "1" && cdnResponse.data.files && cdnResponse.data.files.length > 0) {
                
                const uploadedFile = cdnResponse.data.files[0];

                const payload = {
                    app_id: appId?.toString(),
                    prachar_type: "VIDEO",
                    base64: "", 
                    content: uploadedFile.file_url, // CDN ની લિંક 
                    create_by: userId?.toString() || "1",
                    cdn_response: cdnResponse.data
                };

                // 🔴 જો પહેલેથી વિડીયો હોય તો તેનો ID પાસ કરો જેથી નવો ન બને, જૂનો જ અપડેટ થાય
                if (videos.length > 0 && videos[0].id) {
                    payload.id = videos[0].id.toString();
                }

                console.log("Saving video to database payload:", payload);

                const response = await axios.post(
                    `${BASE_URL}/api/ins_prachar_master`,
                    payload
                );

                const resData = response.data;
                if (resData.Success === "1" || resData.Success === 1 || resData.success === true) {
                    await fetchVideos();
                } else {
                    alert("Failed to save the video record. Server responded but success was false.");
                }
            } else {
                alert("Failed to upload video to CDN server.");
            }
        } catch (error) {
            console.error("Error uploading video:", error);
            alert("An error occurred while uploading the video.");
        } finally {
            setLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // --- Individual Card Delete Action ---
    const handleDeleteVideo = async (id) => {
        if (window.confirm("Are you sure you want to delete this video record?")) {
            try {
                const payload = {
                    app_id: appId?.toString(),
                    id: id?.toString(),
                    delete_by: userId?.toString() || "1"
                };

                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    payload
                );

                const resData = response.data;
                if (resData.Success === "1" || resData.Success === 1 || resData.success === true) {
                    fetchVideos();
                } else {
                    alert("Failed to delete the video. Server responded but success was false.");
                }
            } catch (error) {
                console.error("Error deleting video:", error.response ? error.response.data : error.message);
                alert(`An error occurred while deleting the video: ${error.message}`);
            }
        }
    };

    // --- Expand Comma-Separated Videos & Restrict to 1 ---
    const expandedVideos = videos.flatMap(record => {
        if (!record.content) return [];
        const links = record.content.split(',').map(l => l.trim()).filter(l => l);
        return links.map((link, index) => ({
            ...record,
            singleLink: link,
            uniqueKey: `${record.id}-${index}`
        }));
    });

    // 🔴 માત્ર એક જ વિડીયો બતાવવા માટે (Slice)
    const displayVideo = expandedVideos.slice(0, 1);
    const hasExistingVideo = displayVideo.length > 0;

    // --- Loading State ---
    if (loading && videos.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #ef4444; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4 pb-5'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar (Subtitle + Add/Change Button) */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">
                    <input
                        type="file"
                        accept="video/mp4,video/x-m4v,video/*"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    {/* ALWAYS SHOW ADD/CHANGE BUTTON IF NOT SUB ADMIN */}
                    {!isSubAdmin && (
                        <button 
                            className={`global-btn ${hasExistingVideo ? 'btn-change' : 'btn-add'}`} 
                            onClick={handleAddVideoClick}
                            disabled={loading}
                        >
                            {/* 🔴 આઇકન અને ટેક્સ્ટ બન્ને કન્ડીશન મુજબ બદલાશે */}
                            {hasExistingVideo ? (
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <path d="M12 20h9"></path>
                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                </svg>
                            ) : (
                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            )}
                            {loading ? txt.uploading : (hasExistingVideo ? txt.btnChangeVideo : txt.btnAdd)}
                        </button>
                    )}
                </div>
            </div>

            {/* No Data State */}
            {displayVideo.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">🎬</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- SINGLE VIDEO GRID --- */}
            <div className="video-grid pb-5">
                {displayVideo.map((video) => (
                    <div className="video-card" key={video.uniqueKey}>
                        {/* Video Container */}
                        <div className="media-wrapper">
                            {isDirectVideoFile(video.singleLink) ? (
                                <video 
                                    className="promo-media"
                                    src={video.singleLink}
                                    controls
                                    preload="metadata"
                                >
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <iframe 
                                    className="promo-media"
                                    src={getYouTubeEmbedUrl(video.singleLink)}
                                    title={video.name || "Video"}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            )}
                        </div>

                        {/* Uploader Details Container */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{video.create_by_name || video.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {video.designation || "User"}
                            </div>

                            {/* Delete Button inside Card */}
                            {!isSubAdmin && (
                                <>
                                    <div className="action-divider"></div>
                                    <div className="action-row">
                                        <button 
                                            className="card-delete-btn" 
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                handleDeleteVideo(video.id);
                                            }}
                                        >
                                            <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                                <line x1="14" y1="11" x2="14" y2="17"></line>
                                            </svg>
                                            {txt.btnDelete}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PAGE ACTION BAR --- */
                .page-action-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .subtitle-text {
                    font-size: 15px;
                    color: #475569;
                    font-weight: 600;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .global-buttons {
                    display: flex;
                    gap: 12px;
                }

                .global-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                
                .global-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .global-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Add Button (Red Theme) */
                .btn-add {
                    background: #ef4444;
                    color: #ffffff;
                    border: 1px solid #dc2626;
                    box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.2);
                }
                .btn-add:hover:not(:disabled) {
                    background: #dc2626;
                    border-color: #b91c1c;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(239, 68, 68, 0.3);
                }
                .btn-add:active:not(:disabled) {
                    background: #b91c1c;
                    transform: translateY(0);
                }

                /* Change Button (Blue Theme) */
                .btn-change {
                    background: #3b82f6;
                    color: #ffffff;
                    border: 1px solid #2563eb;
                    box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
                }
                .btn-change:hover:not(:disabled) {
                    background: #2563eb;
                    border-color: #1d4ed8;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(59, 130, 246, 0.3);
                }
                .btn-change:active:not(:disabled) {
                    background: #1d4ed8;
                    transform: translateY(0);
                }

                /* --- VIDEO GRID --- */
                .video-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 24px;
                }

                .video-card {
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .video-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                .media-wrapper {
                    width: 100%;
                    height: 220px;
                    background: #0f172a; 
                    overflow: hidden;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .promo-media {
                    width: 100%;
                    height: 100%;
                    outline: none;
                    border: none;
                }

                .info-wrapper {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #ffffff;
                }

                .upload-info {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .upload-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .upload-name {
                    font-size: 15px;
                    color: #0f172a;
                    font-weight: 700;
                }

                /* Designation Badge */
                .designation-badge {
                    align-self: flex-start;
                    font-size: 11px;
                    font-weight: 700;
                    color: #ef4444; 
                    background: #fef2f2;
                    padding: 4px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border: 1px solid #fecaca;
                }

                /* --- CARD DELETE BUTTON --- */
                .action-divider {
                    height: 1px;
                    background: #f1f5f9;
                    margin: 8px 0 4px 0;
                }
                
                .action-row {
                    display: flex;
                    gap: 10px;
                }

                .card-delete-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    flex: 1;
                    padding: 8px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    background: #fef2f2;
                    color: #ef4444;
                }
                .card-delete-btn:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }
                .card-delete-btn:active {
                    background: #fecaca;
                }
                .card-delete-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Empty State */
                .empty-state { 
                    background: white; 
                    border-radius: 20px; 
                    border: 2px dashed #cbd5e1; 
                }
                .empty-icon { 
                    font-size: 3rem; 
                    margin-bottom: 10px; 
                    opacity: 0.5; 
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .page-action-bar {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .global-buttons {
                        width: 100%;
                        display: flex;
                    }
                    .global-btn {
                        flex: 1;
                        justify-content: center;
                    }
                    .video-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px;
                    }
                    .media-wrapper {
                        height: 250px;
                    }
                }
            `}</style>
        </div>
    );
};

export default VideoPromotion;